import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ChevronLeft } from 'lucide-react'
import { adminApi } from '@/utils/api'

type Option = { id: string; text: string; value: number }
type Response = {
  questionId: string
  questionText: string
  questionType?: string
  answer: string
  score: number
  options?: Option[]
  answeredAt?: string
}
type Section = {
  sectionNumber: number
  sectionName: string
  category: string
  totalScore: number
  riskLevel: string
  isCompleted: boolean
  responses: Response[]
}
type Assessment = {
  id: string
  coupleId: string
  contractNumber: string
  organizationName: string
  status: string
  currentSection: number
  totalSections: number
  progressPercentage: number
  scores?: Record<string, number>
  riskLevels?: Record<string, string>
  startedAt?: string
  completedAt?: string | null
}
type UserInfo = {
  userId: string
  firstName: string
  lastName: string
  email: string
  gender: string
}
type LearningModule = {
  moduleId: string
  moduleTitle: string
  category: string
  progressPercentage: number
  isCompleted: boolean
  completedVideos: number
  totalVideos: number
}
type VideoProgress = {
  videoId: string
  videoTitle: string
  watchedSeconds: number
  totalDuration: number
  watchPercentage: number
  isCompleted: boolean
  completedAt?: string | null
}
type DetailedData = {
  assessment: Assessment
  user: UserInfo
  sections: Section[]
  learningProgress: LearningModule[]
  videoProgress: VideoProgress[]
}

const riskColor = (level?: string) => {
  if (!level) return 'text-gray-500'
  const l = level.toLowerCase()
  if (l === 'high') return 'text-red-600 font-semibold'
  if (l === 'moderate') return 'text-[#FF6600] font-semibold'
  return 'text-[#15803d]'
}

const riskBg = (level?: string) => {
  if (!level) return 'bg-[rgba(237,239,243,0.5)]'
  const l = level.toLowerCase()
  if (l === 'high') return 'bg-red-50'
  if (l === 'moderate') return 'bg-orange-50'
  return 'bg-green-50'
}

const statusBadge = (status?: string) => {
  const s = (status || '').toLowerCase().replace(/[-\s]/g, '_')
  if (s === 'completed') return 'bg-[#d1f4e0] text-[#15803d]'
  if (s === 'in_progress') return 'bg-[#fef3c7] text-[#d97706]'
  return 'bg-[#ffedd5] text-[#ea580c]'
}

const formatDate = (d?: string | null) => {
  if (!d) return '-'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function AdminAssessmentDetail() {
  const { assessmentId } = useParams<{ assessmentId: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<DetailedData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedSection, setExpandedSection] = useState<number | null>(null)

  useEffect(() => {
    if (!assessmentId) {
      setError('Missing assessment ID')
      setLoading(false)
      return
    }
    const fetch = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await adminApi.assessmentDetailed(assessmentId)
        const payload = (res as { data?: DetailedData }).data || (res as unknown as DetailedData)
        setData(payload)
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load assessment details')
      } finally {
        setLoading(false)
      }
    }
    void fetch()
  }, [assessmentId])

  if (loading) {
    return (
      <div className='w-full min-h-screen bg-[#edeff3] flex items-center justify-center'>
        <p className='text-[#4e4d4d]'>Loading assessment details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='w-full min-h-screen bg-[#edeff3] p-4 md:p-6 lg:p-8'>
        <button onClick={() => navigate(-1)} className='flex items-center gap-2 text-[14px] text-[#4e4d4d] hover:text-[#151515] mb-4'>
          <ChevronLeft className='h-4 w-4' /> Back
        </button>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 text-center'>
          <p className='text-red-600'>{error}</p>
        </div>
      </div>
    )
  }

  const assessment = data?.assessment
  const user = data?.user
  const sections = data?.sections || []
  const learningProgress = data?.learningProgress || []
  const videoProgress = data?.videoProgress || []

  return (
    <div className='w-full min-h-screen bg-[#edeff3] p-4 md:p-6 lg:p-8'>
      <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
        {/* Header */}
        <div className='flex items-center gap-4'>
          <button
            onClick={() => navigate(-1)}
            className='w-[32px] h-[32px] bg-[#eaeaea] rounded-full flex items-center justify-center hover:bg-[#d9d9d9]'
          >
            <ChevronLeft className='h-5 w-5' />
          </button>
          <div>
            <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Assessment Detail</h1>
            <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>
              {user?.firstName} {user?.lastName} ({user?.gender}) &middot; {assessment?.contractNumber} &middot; {assessment?.organizationName}
            </p>
          </div>
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium capitalize ${statusBadge(assessment?.status)}`}>
            {assessment?.status?.replace(/_/g, ' ') || 'Unknown'}
          </span>
        </div>

        {/* Quick links */}
        <div className='flex gap-2 text-xs'>
          {assessment?.coupleId && (
            <button
              onClick={() => navigate(`/admin/couples/${assessment.coupleId}`)}
              className='px-3 py-1 rounded-[8px] bg-[#eaeaea] hover:bg-[#d9d9d9] text-[#151515] font-medium'
            >
              View Couple
            </button>
          )}
          {user?.userId && (
            <button
              onClick={() => navigate(`/admin/users/${user.userId}`)}
              className='px-3 py-1 rounded-[8px] bg-[#eaeaea] hover:bg-[#d9d9d9] text-[#151515] font-medium'
            >
              View User
            </button>
          )}
        </div>

        {/* Assessment Overview */}
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d]'>Progress</p>
            <p className='text-[24px] font-bold text-[#151515]'>{assessment?.progressPercentage ?? 0}%</p>
            <div className='w-full h-[4px] bg-[#4e4d4d] bg-opacity-25 rounded-full mt-1'>
              <div className='h-full bg-[#FF6600] rounded-full' style={{ width: `${assessment?.progressPercentage ?? 0}%` }} />
            </div>
          </div>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d]'>Sections</p>
            <p className='text-[24px] font-bold text-[#151515]'>{assessment?.currentSection}/{assessment?.totalSections}</p>
          </div>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d]'>Started</p>
            <p className='text-[14px] font-semibold text-[#151515]'>{formatDate(assessment?.startedAt)}</p>
          </div>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d]'>Completed</p>
            <p className='text-[14px] font-semibold text-[#151515]'>{formatDate(assessment?.completedAt)}</p>
          </div>
        </div>

        {/* Overall Scores */}
        {assessment?.scores && (
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4'>
            <h2 className='text-[18px] font-bold text-[#151515] mb-4'>Overall Scores & Risk Levels</h2>
            <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'>
              {Object.entries(assessment.scores).map(([key, value]) => (
                <div key={key} className={`rounded-[10px] px-4 py-3 ${riskBg(assessment.riskLevels?.[key])}`}>
                  <p className='text-[13px] text-[#475467] capitalize mb-1'>{key.replace(/_/g, ' ')}</p>
                  <p className='text-xl font-bold text-[#151515]'>{value}</p>
                  <p className={`text-xs capitalize ${riskColor(assessment.riskLevels?.[key])}`}>
                    {assessment.riskLevels?.[key] || '-'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sections with Responses */}
        <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4'>
          <h2 className='text-[18px] font-bold text-[#151515] mb-4'>Sections & Responses</h2>
          <div className='space-y-3'>
            {sections.map((section) => (
              <div key={section.sectionNumber} className='bg-white rounded-[10px] border border-[#e8e8e8] overflow-hidden'>
                <button
                  onClick={() => setExpandedSection(expandedSection === section.sectionNumber ? null : section.sectionNumber)}
                  className='w-full flex items-center justify-between px-4 py-3 hover:bg-[#f9fafb] text-left'
                >
                  <div className='flex items-center gap-3'>
                    <span className='w-7 h-7 rounded-full bg-[#151515] text-white text-xs flex items-center justify-center font-semibold'>
                      {section.sectionNumber}
                    </span>
                    <div>
                      <p className='text-[14px] font-semibold text-[#101828]'>{section.sectionName}</p>
                      <p className='text-xs text-[#4e4d4d]'>
                        Score: {section.totalScore} &middot;{' '}
                        <span className={riskColor(section.riskLevel)}>{section.riskLevel}</span>
                        {section.isCompleted && <span className='ml-2 text-[#15803d]'>Completed</span>}
                      </p>
                    </div>
                  </div>
                  <svg
                    className={`w-4 h-4 text-[#475467] transition-transform ${expandedSection === section.sectionNumber ? 'rotate-180' : ''}`}
                    fill='none' viewBox='0 0 24 24' stroke='currentColor'
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                  </svg>
                </button>

                {expandedSection === section.sectionNumber && (
                  <div className='px-4 pb-4 space-y-3'>
                    {section.responses.map((r, idx) => (
                      <div key={r.questionId || idx} className='bg-[rgba(237,239,243,0.5)] rounded-[8px] p-3'>
                        <p className='text-[14px] text-[#101828] mb-2'>{r.questionText}</p>
                        <div className='flex flex-wrap gap-2'>
                          {r.options?.map((opt) => (
                            <span
                              key={opt.id}
                              className={`px-2 py-1 rounded text-xs ${
                                opt.text === r.answer
                                  ? 'bg-[#FF6600] text-white font-semibold'
                                  : 'bg-white border border-[#e8e8e8] text-[#475467]'
                              }`}
                            >
                              {opt.text} ({opt.value})
                            </span>
                          ))}
                        </div>
                        <div className='flex gap-4 mt-2 text-xs text-[#4e4d4d]'>
                          <span>Answer: <strong className='text-[#101828]'>{r.answer}</strong></span>
                          <span>Score: <strong className='text-[#101828]'>{r.score}</strong></span>
                          {r.answeredAt && <span>{formatDate(r.answeredAt)}</span>}
                        </div>
                      </div>
                    ))}
                    {section.responses.length === 0 && (
                      <p className='text-[14px] text-[#4e4d4d] py-2'>No responses recorded</p>
                    )}
                  </div>
                )}
              </div>
            ))}
            {sections.length === 0 && (
              <p className='text-[14px] text-[#4e4d4d]'>No sections data available</p>
            )}
          </div>
        </div>

        {/* Learning & Video Progress */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {/* Learning Modules */}
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4'>
            <h2 className='text-[18px] font-bold text-[#151515] mb-4'>Learning Progress</h2>
            {learningProgress.length === 0 ? (
              <p className='text-[14px] text-[#4e4d4d]'>No learning data</p>
            ) : (
              <div className='space-y-3'>
                {learningProgress.map((m) => (
                  <div key={m.moduleId} className='flex items-center justify-between bg-white rounded-[10px] border border-[#e8e8e8] px-3 py-2'>
                    <div>
                      <p className='text-[14px] font-medium text-[#101828]'>{m.moduleTitle}</p>
                      <p className='text-xs text-[#4e4d4d] capitalize'>{m.category.replace(/_/g, ' ')} &middot; {m.completedVideos}/{m.totalVideos} videos</p>
                    </div>
                    <div className='text-right'>
                      <p className='text-[14px] font-bold text-[#101828]'>{m.progressPercentage}%</p>
                      <p className={`text-xs ${m.isCompleted ? 'text-[#15803d]' : 'text-[#FF6600]'}`}>
                        {m.isCompleted ? 'Done' : 'In Progress'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Video Progress */}
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4'>
            <h2 className='text-[18px] font-bold text-[#151515] mb-4'>Video Progress</h2>
            {videoProgress.length === 0 ? (
              <p className='text-[14px] text-[#4e4d4d]'>No video progress data</p>
            ) : (
              <div className='space-y-3'>
                {videoProgress.map((v) => (
                  <div key={v.videoId} className='bg-white rounded-[10px] border border-[#e8e8e8] px-3 py-2'>
                    <div className='flex items-center justify-between mb-1'>
                      <p className='text-[14px] font-medium text-[#101828]'>{v.videoTitle}</p>
                      <span className={`text-xs ${v.isCompleted ? 'text-[#15803d] font-semibold' : 'text-[#4e4d4d]'}`}>
                        {v.isCompleted ? 'Completed' : `${v.watchPercentage}%`}
                      </span>
                    </div>
                    <div className='w-full h-[4px] bg-[#4e4d4d] bg-opacity-25 rounded-full'>
                      <div
                        className={`h-full rounded-full ${v.isCompleted ? 'bg-[#15803d]' : 'bg-[#FF6600]'}`}
                        style={{ width: `${v.watchPercentage}%` }}
                      />
                    </div>
                    <p className='text-xs text-[#4e4d4d] mt-1'>
                      {formatTime(v.watchedSeconds)} / {formatTime(v.totalDuration)}
                      {v.completedAt && ` - Completed ${formatDate(v.completedAt)}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
